/* eslint-disable react-hooks/immutability -- ReactiveProperty.value is an imperative setter for cross-framework reactivity */
/**
 * User settings integration with the Properties system.
 * Registers user-level properties (color, units) and provides hooks/accessors
 * for getting and setting them across the application.
 *
 * Uses ReactiveProperty for cross-framework reactivity (React + Potree).
 */

import { useContext, useEffect, useRef } from 'react';
import { propertyRegistry, PropertyType, PropertyValueType } from '../index';
import { hexToColor as utilHexToColor, colorToHex as utilColorToHex } from '../../../utils/colorUtils';
import { Color, MeasurementUnits } from '../../../types/graphqlTypes';
import { UserContext } from '../../../contexts/UserContext';
import { PropertyValueData } from '../types';
import { ReactiveProperty } from '../reactive/ReactiveProperty';
import { getGlobalPropertyService, hasGlobalPropertyService } from '../reactive/globalPropertyService';
import { useReactiveProperty } from '../reactive/useReactiveProperty';
import { useQuery } from '@apollo/client/react';
import { GET_USER_PROPERTIES } from '../../../graphql/properties';
import type { PropertyGroup } from '../hooks/usePropertyManager';

// ============================================================================
// CONSTANTS
// ============================================================================

// Default color for user annotations (purple)
const DEFAULT_OBJECT_COLOR: Color = { r: 150, g: 0, b: 250, a: 1 };
const DEFAULT_OBJECT_COLOR_HEX = '#9600fa';
const DEFAULT_UNIT = MeasurementUnits.Meters;

// ============================================================================
// DEFAULT COLOR — PROPERTY REGISTRATION
// ============================================================================

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_default_object_color',
  name: 'Default Object Color',
  valueType: PropertyValueType.COLOR,
  defaultValue: DEFAULT_OBJECT_COLOR_HEX,
  description: 'Default color for newly created annotations',
  ui: { group: 'Appearance' },
});

// ============================================================================
// DEFAULT COLOR — GLOBAL ACCESSORS
// ============================================================================

/**
 * Get reactive property for user default object color.
 * Can be called from anywhere (React, Potree, etc.) once the global service is initialized.
 */
export function getUserDefaultColorProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('user_default_object_color', '', PropertyType.USER_SETTINGS);
}

/** Get the default color constant as hex string. */
export function getDefaultObjectColorHex(): string {
  return DEFAULT_OBJECT_COLOR_HEX;
}

/** Get the default color constant as Color object. */
export function getDefaultObjectColor(): Color {
  return DEFAULT_OBJECT_COLOR;
}

// ============================================================================
// DEFAULT COLOR — REACT HOOK
// ============================================================================

/**
 * Hook to get/set the user's default object color.
 * Backed by ReactiveProperty for automatic persistence and cross-component sync.
 * DefaultColors in defines.ts reads directly from the property service.
 */
export const useUserDefaultColor = () => {
  const [colorValue, setColorValue] = useReactiveProperty({
    projectId: '',
    identifier: 'user_default_object_color',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  const currentColor = (colorValue as string) || DEFAULT_OBJECT_COLOR_HEX;

  const setColorHex = (hex: PropertyValueData) => {
    setColorValue(hex);
  };

  return {
    colorHex: currentColor,
    setColorHex,
    colorObject: utilHexToColor(currentColor),
    setColorObject: (color: Color) => setColorHex(utilColorToHex(color)),
  };
};

// ============================================================================
// MEASUREMENT UNIT — PROPERTY REGISTRATION
// ============================================================================

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_measurement_unit',
  name: 'Measurement Unit',
  valueType: PropertyValueType.SELECT,
  defaultValue: DEFAULT_UNIT,
  description: 'Unit system for measurements',
  validation: {
    options: [
      { value: MeasurementUnits.Meters, label: 'Metric' },
      { value: MeasurementUnits.Feet, label: 'Imperial' },
    ],
  },
  ui: { group: 'Units' },
});

// ============================================================================
// MEASUREMENT UNIT — GLOBAL ACCESSOR
// ============================================================================

/**
 * Get reactive property for measurement unit preference.
 * Can be called from anywhere (React, Potree, etc.) once the global service is initialized.
 */
export function getUserMeasurementUnitProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('user_measurement_unit', '', PropertyType.USER_SETTINGS);
}

// ============================================================================
// MEASUREMENT UNIT — REACT HOOK
// ============================================================================

/**
 * Hook to get/set the user's measurement unit preference.
 * Backed by ReactiveProperty for automatic persistence and cross-component sync.
 */
export const useUnitPreference = () => {
  const [unitValue, setUnitValue, unitProperty] = useReactiveProperty({
    projectId: '',
    identifier: 'user_measurement_unit',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  // Migrate from legacy UserContext.measurementUnit on first load
  const { measurementUnit: legacyUnit } = useContext(UserContext);
  const migrated = useRef(false);
  useEffect(() => {
    if (!migrated.current && !unitValue && legacyUnit === MeasurementUnits.Feet) {
      migrated.current = true;
      unitProperty.value = MeasurementUnits.Feet;
    }
  }, [unitValue, legacyUnit, unitProperty]);

  const currentUnit = (unitValue as MeasurementUnits) || DEFAULT_UNIT;

  return {
    unit: currentUnit,
    setUnit: setUnitValue,
    isFeet: currentUnit === MeasurementUnits.Feet,
  };
};

// ============================================================================
// DARK MODE — PROPERTY REGISTRATION
// ============================================================================
//
// Migrated from the legacy `users.darkMode` column (the same move the
// measurement unit made from UserContext.measurementUnit): this property is
// the single source of truth for the theme. The settings modal writes it,
// `useDarkModeTheme` at the app root applies it to the DOM (`dark` class +
// `localStorage.theme` for main.tsx's boot-time FOUC guard), and non-React
// code (Potree view modes, the vanilla tool settings panel) subscribes for
// live updates. The legacy column was copied into this property by the
// `2026-08-13_migrate_dark_mode_to_property` DB migration and then dropped.

function readLegacyDarkModePreference(): boolean {
  if (typeof localStorage !== 'undefined' && (localStorage.theme === 'dark' || localStorage.theme === 'light')) {
    return localStorage.theme === 'dark';
  }
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_dark_mode',
  name: 'Dark Mode',
  valueType: PropertyValueType.BOOLEAN,
  defaultValue: false,
  getDefaultValue: readLegacyDarkModePreference,
  description: 'Dark color theme for the application',
  ui: { group: 'Appearance' },
});

// ============================================================================
// DARK MODE — GLOBAL ACCESSOR + REACT HOOKS
// ============================================================================

/**
 * Get the live dark-mode property. Callable from anywhere (React, Potree,
 * vanilla panels) once the global service is initialized.
 */
export function getUserDarkModeProperty(): ReactiveProperty<boolean> {
  return getGlobalPropertyService().getProperty('user_dark_mode', '', PropertyType.USER_SETTINGS) as ReactiveProperty<boolean>;
}

/**
 * Hook to get/set the user's dark-mode preference. Backed by ReactiveProperty
 * for automatic persistence and cross-component sync; the DOM side effects
 * (dark class, localStorage) are applied centrally by `useDarkModeTheme`.
 */
export const useDarkModePreference = () => {
  const [darkValue, setDarkValue] = useReactiveProperty({
    projectId: '',
    identifier: 'user_dark_mode',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  return {
    darkMode: darkValue === true,
    setDarkMode: (dark: boolean) => setDarkValue(dark),
  };
};

/**
 * App-root theme driver — call exactly once (App). Applies every property
 * change to the DOM: `localStorage.theme` first (main.tsx's boot FOUC guard
 * and the legacy fallbacks read it), then the `dark` class on <html>
 * (Tailwind styling + the class observers that sync the viewer background).
 */
export const useDarkModeTheme = (): void => {
  const [, , property] = useReactiveProperty({
    projectId: '',
    identifier: 'user_dark_mode',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  useEffect(() => {
    const subscription = property.subscribe((dark) => {
      const isDark = dark === true;
      if (typeof localStorage !== 'undefined') localStorage.theme = isDark ? 'dark' : 'light';
      document.documentElement.classList.toggle('dark', isDark);
    });
    return () => subscription.unsubscribe();
  }, [property]);
};

/**
 * App-root loader for the user's own settings — call exactly once (App). Inside a project,
 * ProjectPropertyProvider loads them with everything else; everywhere else (the projects list,
 * settings modal, ...) nothing did, so a setting read before the query came back kept its
 * default after every refresh. preloadValues also fills properties created before the data
 * arrived, unless they were changed meanwhile.
 */
export const usePreloadUserSettings = (): void => {
  const { data } = useQuery<{ currentUser?: { id: string; properties?: PropertyGroup[] } }>(GET_USER_PROPERTIES, { fetchPolicy: 'no-cache' });
  const preloaded = useRef(false);
  useEffect(() => {
    const groups = data?.currentUser?.properties;
    if (preloaded.current || !groups || !hasGlobalPropertyService()) return;
    getGlobalPropertyService().preloadValues(groups, PropertyType.USER_SETTINGS);
    preloaded.current = true;
  }, [data]);
};

// ============================================================================
// NAVIGATION MODE — PROPERTY REGISTRATION + ACCESSORS
// ============================================================================
//
// How the scroll wheel navigates the 2D (plan) views; 3D always wheel-zooms:
//  - TOUCHPAD (default — the behavior 2D always had): wheel pans the view
//    (two-finger trackpad scroll, both axes), ctrl+wheel (which is also how
//    trackpad pinch arrives) zooms toward the cursor.
//  - MOUSE: wheel zooms toward the cursor — the classic discrete
//    mouse-wheel behavior.

export enum NavigationMode {
  Mouse = 'MOUSE',
  Touchpad = 'TOUCHPAD',
}

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_navigation_mode',
  name: 'Navigation Mode',
  valueType: PropertyValueType.SELECT,
  defaultValue: NavigationMode.Touchpad,
  description: 'How the scroll wheel navigates the view',
  validation: {
    options: [
      { value: NavigationMode.Touchpad, label: 'Touchpad' },
      { value: NavigationMode.Mouse, label: 'Mouse' },
    ],
  },
  ui: { group: 'Navigation' },
});

/**
 * Get the live navigation-mode property. Callable from anywhere (React,
 * Potree controls) once the global service is initialized.
 */
export function getUserNavigationModeProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('user_navigation_mode', '', PropertyType.USER_SETTINGS);
}

/**
 * Synchronous read for hot paths (wheel handlers) — safe before the property
 * service exists (falls back to the Touchpad default).
 */
export function isTouchpadNavigation(): boolean {
  if (!hasGlobalPropertyService()) return true;
  try {
    return getUserNavigationModeProperty().value !== NavigationMode.Mouse;
  } catch {
    return true;
  }
}

/** Hook to get/set the user's navigation-mode preference. */
export const useNavigationModePreference = () => {
  const [modeValue, setModeValue] = useReactiveProperty({
    projectId: '',
    identifier: 'user_navigation_mode',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  return {
    navigationMode: modeValue === NavigationMode.Mouse ? NavigationMode.Mouse : NavigationMode.Touchpad,
    setNavigationMode: (mode: NavigationMode) => setModeValue(mode),
  };
};

// ============================================================================
// HIDE DEBUG BUTTON — PROPERTY REGISTRATION (super-admin only)
// ============================================================================

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_hide_debug_button',
  name: 'Hide Debug Button',
  valueType: PropertyValueType.BOOLEAN,
  defaultValue: true,
  description: 'Hide the super-admin Debug button from the renderer header',
  ui: { group: 'Debug' },
});

// ============================================================================
// HIDE DEBUG BUTTON — REACT HOOK
// ============================================================================

/**
 * Hook to get/set the super admin's "hide debug button" preference.
 * Backed by ReactiveProperty for automatic persistence and cross-component sync.
 */
export const useHideDebugButton = () => {
  const [hideValue, setHideValue] = useReactiveProperty({
    projectId: '',
    identifier: 'user_hide_debug_button',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  return {
    // Default to `true` (hidden) when the property hasn't been set yet.
    hideDebugButton: typeof hideValue === 'boolean' ? hideValue : true,
    setHideDebugButton: (hide: boolean) => setHideValue(hide),
  };
};

// ============================================================================
// SHOW DEBUG UI — PROPERTY REGISTRATION (super-admin only)
// ============================================================================

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'user_show_debug_ui',
  name: 'Show Debug UI',
  valueType: PropertyValueType.BOOLEAN,
  defaultValue: false,
  description: 'Show developer debug UI (e.g. wall-sketch hover info panels) in the viewer',
  ui: { group: 'Debug' },
});

// ============================================================================
// SHOW DEBUG UI — GLOBAL ACCESSOR + REACT HOOK
// ============================================================================

/**
 * Get reactive property for the super admin's "show debug UI" preference.
 * Can be called from anywhere (React, Potree, etc.) once the global service
 * is initialized.
 */
export function getShowDebugUiProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('user_show_debug_ui', '', PropertyType.USER_SETTINGS);
}

/**
 * Hook to get/set the super admin's "show debug UI" preference.
 * Backed by ReactiveProperty for automatic persistence and cross-component sync.
 */
export const useShowDebugUi = () => {
  const [showValue, setShowValue] = useReactiveProperty({
    projectId: '',
    identifier: 'user_show_debug_ui',
    objectIdentifier: '',
    propertyType: PropertyType.USER_SETTINGS,
  });

  return {
    // Default to `false` (hidden) when the property hasn't been set yet.
    showDebugUi: showValue === true,
    setShowDebugUi: (show: boolean) => setShowValue(show),
  };
};

// NOTE: the former `user_floorplan_representation` USER setting was replaced
// by the per-floor `floor_wall_source` property (the Floor panel's "Source"
// select) — the Wall sketch | Measured toggle now reads/writes that instead.

// ============================================================================
// OPENING SKETCH DIMENSIONS — PROPERTY REGISTRATION
// ============================================================================
//
// The Layout persona's opening-sketch draw tool remembers, per user, the
// "fixed length" toggle plus the length / lower bound / upper bound values —
// with a SEPARATE remembered set per opening kind (door / window).
//
// One registered property can only hold one `defaultValue`, so to give doors
// and windows their own kind-appropriate defaults we register each of the four
// settings twice — once per kind (identifier suffixed `_door` / `_window`).
// Each property then owns its own default; the tool just reads `.value` and
// needs no local fallback tables. The accessors below route `kind` → identifier.

/** Per-kind default fixed length (m). Doors ~0.9 m wide; windows a bit wider. */
const DEFAULT_LENGTH = { DOOR: 0.9, WINDOW: 1.2 } as const;
/** Per-kind default sill / head heights (m above floor). A door runs floor→2.1 m;
 *  a typical window sits on a ~0.9 m sill with a ~1.5 m head (≈0.6 m tall). */
const DEFAULT_LOWER = { DOOR: 0, WINDOW: 0.9 } as const;
const DEFAULT_UPPER = { DOOR: 2.1, WINDOW: 1.5 } as const;

type OpeningKindKey = 'DOOR' | 'WINDOW';
const OPENING_KINDS: OpeningKindKey[] = ['DOOR', 'WINDOW'];

/** Registered property identifiers, per param, per kind. */
export const OPENING_SKETCH_PROPERTY_IDS = {
  fixedLength: { DOOR: 'user_opening_sketch_fixed_length_door', WINDOW: 'user_opening_sketch_fixed_length_window' },
  length: { DOOR: 'user_opening_sketch_length_door', WINDOW: 'user_opening_sketch_length_window' },
  lowerBound: { DOOR: 'user_opening_sketch_lower_bound_door', WINDOW: 'user_opening_sketch_lower_bound_window' },
  upperBound: { DOOR: 'user_opening_sketch_upper_bound_door', WINDOW: 'user_opening_sketch_upper_bound_window' },
} as const;

for (const kind of OPENING_KINDS) {
  const label = kind === 'DOOR' ? 'Door' : 'Window';
  propertyRegistry.register({
    type: PropertyType.USER_SETTINGS,
    identifier: OPENING_SKETCH_PROPERTY_IDS.fixedLength[kind],
    name: `Opening Sketch Fixed Length (${label})`,
    valueType: PropertyValueType.BOOLEAN,
    defaultValue: false,
    description: 'Whether the opening sketch tool uses a fixed length instead of free-drawing',
    ui: { group: 'Layout' },
  });
  propertyRegistry.register({
    type: PropertyType.USER_SETTINGS,
    identifier: OPENING_SKETCH_PROPERTY_IDS.length[kind],
    name: `Opening Sketch Length (${label})`,
    valueType: PropertyValueType.FLOAT,
    defaultValue: DEFAULT_LENGTH[kind],
    description: 'Fixed opening length (m) along the wall',
    ui: { group: 'Layout' },
  });
  propertyRegistry.register({
    type: PropertyType.USER_SETTINGS,
    identifier: OPENING_SKETCH_PROPERTY_IDS.lowerBound[kind],
    name: `Opening Sketch Lower Bound (${label})`,
    valueType: PropertyValueType.FLOAT,
    defaultValue: DEFAULT_LOWER[kind],
    description: 'Opening sill height (m) above the floor',
    ui: { group: 'Layout' },
  });
  propertyRegistry.register({
    type: PropertyType.USER_SETTINGS,
    identifier: OPENING_SKETCH_PROPERTY_IDS.upperBound[kind],
    name: `Opening Sketch Upper Bound (${label})`,
    valueType: PropertyValueType.FLOAT,
    defaultValue: DEFAULT_UPPER[kind],
    description: 'Opening head height (m) above the floor',
    ui: { group: 'Layout' },
  });
}

// ============================================================================
// OPENING SKETCH DIMENSIONS — GLOBAL ACCESSORS (per opening kind)
// ============================================================================
//
// `kind` ('DOOR' | 'WINDOW') selects the registered property for that kind.
// Each property owns its own default, so `.value` returns the right default
// until the user sets one. Callable from Potree tool code once the global
// service is initialized.

function openingSketchProp(id: string): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty(id, '', PropertyType.USER_SETTINGS);
}

/** Normalize any kind string to a registered key (defaults to DOOR). */
function kindKey(kind: string): OpeningKindKey {
  return kind === 'WINDOW' ? 'WINDOW' : 'DOOR';
}

export function getOpeningSketchFixedLengthProperty(kind: string): ReactiveProperty<PropertyValueData> {
  return openingSketchProp(OPENING_SKETCH_PROPERTY_IDS.fixedLength[kindKey(kind)]);
}

export function getOpeningSketchDefaultLengthProperty(kind: string): ReactiveProperty<PropertyValueData> {
  return openingSketchProp(OPENING_SKETCH_PROPERTY_IDS.length[kindKey(kind)]);
}

export function getOpeningSketchLowerBoundProperty(kind: string): ReactiveProperty<PropertyValueData> {
  return openingSketchProp(OPENING_SKETCH_PROPERTY_IDS.lowerBound[kindKey(kind)]);
}

export function getOpeningSketchUpperBoundProperty(kind: string): ReactiveProperty<PropertyValueData> {
  return openingSketchProp(OPENING_SKETCH_PROPERTY_IDS.upperBound[kindKey(kind)]);
}
